import { type Material, type MaterialText, plainMaterialText } from '@shared/material'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { type PageRect } from '@shared/board'
import { useBoardCtx } from '@/board/context'
import { fileUrl } from '@/board/upload'
import { drawRegion, loadPdf } from './pdf'

/** The board's library. */
export function useMaterials(): readonly Material[] {
  const { client } = useBoardCtx()
  return useSyncExternalStore(client.subscribeMaterials, client.getMaterials)
}

export function useMaterial(id: string): Material | undefined {
  return useMaterials().find((m) => m.id === id)
}

export function pdfUrl(boardId: string, m: Material) {
  return fileUrl(boardId, m.fileKey)
}

/** Most pixels a drawn region gets on its long side (a whole page zoomed in stays sharp enough). */
const MAX_DRAW = 3200

/**
 * A canvas showing `rect` of a material's page, drawn from the PDF at
 * `width` CSS pixels times `sharpness` (so zooming in on the board stays crisp).
 */
export function useRegionCanvas(
  material: Material | undefined,
  page: number,
  rect: PageRect,
  width: number,
  sharpness = 2,
) {
  const { boardId } = useBoardCtx()
  const ref = useRef<HTMLCanvasElement>(null)
  const { x, y, w, h } = rect
  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !material) return
    let cancel: (() => void) | null = null
    let gone = false
    const p = material.pages[page - 1]
    const aspect = p ? (h * p.h) / (w * p.w) : 1
    const limit = MAX_DRAW / (window.devicePixelRatio || 1)
    const css = Math.min(width * sharpness, limit, limit / aspect)
    void loadPdf(pdfUrl(boardId, material)).then((doc) => {
      if (gone) return
      cancel = drawRegion(doc, page, { x, y, w, h }, canvas, css)
    })
    return () => {
      gone = true
      cancel?.()
    }
  }, [boardId, material, page, x, y, w, h, width, sharpness])
  return ref
}

const texts = new Map<string, Promise<MaterialText | null>>()

/** A material's text with where each line is (what the AI reads), loaded once. */
export function loadMaterialText(boardId: string, m: Material): Promise<MaterialText | null> {
  let p = texts.get(m.id)
  if (!p) {
    p = fetch(`/api/boards/${boardId}/files/${m.fileKey}/derived/text.json`)
      .then(async (r) => (r.ok ? plainMaterialText((await r.json()) as MaterialText) : null))
      .catch(() => null)
    texts.set(m.id, p)
  }
  return p
}
